import * as React from 'react';
import { Box, FormControlLabel, Grid, MenuItem, Radio, RadioGroup, Switch, TextField, Typography } from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { Field } from 'react-final-form';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import TimerIcon from '@material-ui/icons/Timer';
import { FormSectionCard } from '../../components/cards/FormSectionCard';
import { ConfigureTabProps } from './ConfigureTabProps';

const ORANGE = '#e65100';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        fieldRow: {
            marginBottom: theme.spacing(2.5),
        },
        label: {
            fontSize: 11.5,
            fontWeight: 700,
            color: theme.palette.text.secondary,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            marginBottom: theme.spacing(0.75),
        },
        help: {
            fontSize: 11.5,
            color: theme.palette.text.disabled,
            marginTop: theme.spacing(0.75),
        },
        toggleRow: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 10,
            padding: theme.spacing(1.5, 1.75),
            backgroundColor: theme.palette.grey[50],
        },
        toggleTitle: {
            fontSize: 13,
            fontWeight: 700,
        },
        toggleDesc: {
            fontSize: 11.5,
            color: theme.palette.text.disabled,
            marginTop: 2,
            maxWidth: 420,
        },
        manualNote: {
            border: `1px dashed ${theme.palette.grey[300]}`,
            borderRadius: 10,
            padding: theme.spacing(2),
            fontSize: 12.5,
            color: theme.palette.text.secondary,
        },
    })
);

export const DueDateTab: React.FC<ConfigureTabProps> = ({ values, taskflow }) => {
    const classes = useStyles();
    const { due } = values;

    const otherTasks = taskflow.taskOrder
        .map(taskId => taskflow.tasks[taskId])
        .filter(otherTask => !!otherTask && otherTask.id !== values.id);

    return (
        <FormSectionCard
            title="Due Date"
            description="When this task falls due, relative to the matter or another task."
            color={ORANGE}
            bgColor="#fff3e0"
            icon={TimerIcon}
        >
            <Box className={`${classes.fieldRow} ${classes.toggleRow}`}>
                <Box>
                    <Typography className={classes.toggleTitle}>Manual due date</Typography>
                    <Typography className={classes.toggleDesc}>
                        No formula — the assignee sets this deadline by hand once they see what's actually required.
                    </Typography>
                </Box>
                <Field name="due.manual" type="checkbox" subscription={{ value: true }}>
                    {({ input }) => <Switch {...input} color="primary" checked={input.checked} />}
                </Field>
            </Box>

            {due.manual ? (
                <Box className={classes.manualNote}>
                    <Typography className={classes.label}>Note for whoever sets this deadline</Typography>
                    <Field name="due.manualNote" subscription={{ value: true }}>
                        {({ input }) => (
                            <TextField
                                {...input}
                                fullWidth={true}
                                multiline={true}
                                minRows={2}
                                variant="outlined"
                                size="small"
                                placeholder="e.g. dependent on client requirements"
                            />
                        )}
                    </Field>
                </Box>
            ) : (
                <>
                    <Box className={classes.fieldRow}>
                        <Typography className={classes.label}>Rule</Typography>
                        <Field name="due.mode" subscription={{ value: true }}>
                            {({ input }) => (
                                <RadioGroup row={true} {...input}>
                                    <FormControlLabel value="add" control={<Radio color="primary" size="small" />} label="Add days" />
                                    <FormControlLabel value="sub" control={<Radio color="primary" size="small" />} label="Subtract days" />
                                </RadioGroup>
                            )}
                        </Field>
                    </Box>

                    <Grid container={true} spacing={2}>
                        <Grid item={true} xs={6}>
                            <Box className={classes.fieldRow}>
                                <Typography className={classes.label}>Number of days</Typography>
                                <Field name="due.days" subscription={{ value: true }}>
                                    {({ input }) => <TextField {...input} fullWidth={true} variant="outlined" size="small" />}
                                </Field>
                            </Box>
                        </Grid>
                        <Grid item={true} xs={6}>
                            <Box className={classes.fieldRow}>
                                <Typography className={classes.label}>Basis</Typography>
                                <Field name="due.basis" subscription={{ value: true }}>
                                    {({ input }) => (
                                        <ToggleButtonGroup
                                            exclusive={true}
                                            size="small"
                                            value={input.value}
                                            onChange={(event: React.MouseEvent<HTMLElement>, nextBasis: 'cal' | 'work' | null) => {
                                                if (nextBasis) {
                                                    input.onChange(nextBasis);
                                                }
                                            }}
                                        >
                                            <ToggleButton value="cal">Calendar days</ToggleButton>
                                            <ToggleButton value="work">Working days</ToggleButton>
                                        </ToggleButtonGroup>
                                    )}
                                </Field>
                            </Box>
                        </Grid>
                    </Grid>

                    <Box className={classes.fieldRow}>
                        <Typography className={classes.label}>Relative to</Typography>
                        <Field name="due.relTo" subscription={{ value: true }}>
                            {({ input }) => (
                                <RadioGroup row={true} {...input} style={{ marginBottom: 8 }}>
                                    <FormControlLabel value="matter" control={<Radio color="primary" size="small" />} label="Matter date" />
                                    <FormControlLabel value="member" control={<Radio color="primary" size="small" />} label="Another task in this flow" />
                                </RadioGroup>
                            )}
                        </Field>

                        {due.relTo === 'matter' ? (
                            <Field name="due.relMatterDate" subscription={{ value: true }}>
                                {({ input }) => (
                                    <TextField {...input} select={true} fullWidth={true} variant="outlined" size="small">
                                        {taskflow.matterDateOptions.map(option => (
                                            <MenuItem key={option} value={option}>{option}</MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            </Field>
                        ) : (
                            <Field name="due.relMemberTaskId" subscription={{ value: true }}>
                                {({ input }) => (
                                    <TextField {...input} select={true} fullWidth={true} variant="outlined" size="small">
                                        {otherTasks.map(otherTask => (
                                            <MenuItem key={otherTask.id} value={otherTask.id}>{otherTask.id} · {otherTask.title}</MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            </Field>
                        )}
                    </Box>

                    <Box className={classes.toggleRow}>
                        <Box>
                            <Typography className={classes.toggleTitle}>Lock this deadline</Typography>
                            <Typography className={classes.toggleDesc}>
                                {due.lockDescription || 'A due-date recalculation elsewhere in the flow won\'t move this one.'}
                            </Typography>
                        </Box>
                        <Field name="due.lock" type="checkbox" subscription={{ value: true }}>
                            {({ input }) => <Switch {...input} color="primary" checked={input.checked} />}
                        </Field>
                    </Box>
                </>
            )}
        </FormSectionCard>
    );
};
